/**
 * Pure flow-data derivation for the intent flow page (`/intents/:intentId/flow`).
 *
 * `IntentFlowPage.tsx` renders a static, layered node-link SVG; this module owns the deterministic
 * derivation that feeds it. Beyond the static {nodes, edges} graph it also emits timed `FlowStep`s
 * (one per hop, carrying direction/verdict/checks/latency/epoch) and a provenance seal set — the
 * inputs the later playback, rail, and inspector layers consume. Everything here is a pure function
 * of the intent + its interactions (no physics, no React, no I/O) so it renders the same every time
 * and can be exercised directly by the property tests in tasks 8.2–8.4 and 9.x.
 */

import type { Interaction } from "../types.ts";

/** A node kind drives its colour and column in the layered layout. */
export type NodeKind = "human" | "agent" | "tool" | "provenance";

export interface FlowNode {
  readonly id: string;
  readonly label: string;
  readonly kind: NodeKind;
  /** Column (0 = initiators, 1 = agents, 2 = tools) and vertical slot. */
  readonly col: number;
  readonly x: number;
  readonly y: number;
  readonly threat: boolean;
}

export interface FlowEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly threat: boolean;
}

/** Direction of a single timed hop. */
export type FlowDirection = "request" | "response";
/** Gate outcome of a single timed hop. */
export type FlowVerdict = "allowed" | "blocked";

/**
 * One timed hop in the flow: the animation plays these in time order, lighting the matching
 * edge/node. Derived from an interaction's first step row when present, otherwise synthesized from
 * the flat interaction. `spanId` is a stable id derived from the interaction id.
 */
export interface FlowStep {
  readonly from: string;
  readonly to: string;
  readonly dir: FlowDirection;
  readonly verdict: FlowVerdict;
  readonly checks: { readonly identity: boolean; readonly trust: boolean; readonly scope: boolean };
  readonly latency: number;
  readonly epoch?: number;
  readonly interactionId: string;
  readonly spanId: string;
}

/**
 * The synthetic final "seal" beat: an edge from the last hop's target to a provenance node,
 * derived from the intent's `provenanceRecordID`. Empty when the intent has no provenance record
 * or no hops (behaviour identical to today — no seal rendered).
 */
export interface SealEdge {
  readonly from: string;
  readonly to: string;
  readonly threat: boolean;
}

/**
 * The full derivation result: the existing static graph shape ({nodes, edges, width, height})
 * extended with timed `steps` and the `sealEdges` set.
 */
export interface Flow {
  readonly nodes: readonly FlowNode[];
  readonly edges: readonly FlowEdge[];
  readonly steps: readonly FlowStep[];
  readonly sealEdges: readonly SealEdge[];
  readonly width: number;
  readonly height: number;
}

export const COL_X = [120, 440, 760] as const;
export const ROW_GAP = 92;
/**
 * The x of the synthetic provenance (seal) node's column. It sits to the RIGHT of the tool column
 * (`COL_X[2]`) in its own lane so the seal node is never stacked in a hop column directly under
 * another tile — which previously made its "Seal …" caption overlap the tile's label above it.
 */
export const PROV_X = COL_X[2] + 184 + 96;
export const TOP = 72;
export const NODE_W = 184;
export const NODE_H = 56;

/**
 * Icon-tile geometry (reference-matching render). Each node keeps its NODE_W×NODE_H bounding box for
 * layout + edge math, but RENDERS as a centered rounded-square glyph tile (TILE) with the label as
 * text BENEATH the tile — like the AgentDNA reference. Edges connect at the tile's horizontal mid
 * points (cx ± TILE/2, tile vertical centre), not the box edges.
 */
export const TILE = 44;
/** Horizontal centre of a node's tile within its bounding box. */
export function tileCX(x: number): number {
  return x + NODE_W / 2;
}
/** Vertical centre of a node's tile within its bounding box (tile sits at the top of the box). */
export function tileCY(y: number): number {
  return y + TILE / 2;
}

export const KIND_FILL: Record<NodeKind, string> = {
  human: "var(--accent, #2563eb)",
  agent: "var(--bg-raised)",
  tool: "var(--bg-raised)",
  provenance: "var(--bg-raised)",
};

/** Stable span id derived from an interaction id. */
function spanIdFor(interactionId: string): string {
  return `span-${interactionId}`;
}

/** Node id for the synthetic provenance node of a given provenance record. */
export function provenanceNodeId(provenanceRecordID: string): string {
  return `prov-${provenanceRecordID}`;
}

/**
 * Derive the timed `FlowStep` for a single interaction (Requirements 4.1, 4.2).
 *
 * - 4.1 — When the interaction carries a step row, the step is derived from the FIRST step: its
 *   direction/verdict/checks/epoch carry through, and `latency` is the step's `latencyMs` when
 *   present else the interaction runtime.
 * - 4.2 — When the interaction is stepless, synthesize a `request` step: verdict `blocked` iff the
 *   interaction is a threat else `allowed`; a threat trips trust+scope (identity still passes), a
 *   clean hop passes all three; `latency` is the interaction runtime and there is no epoch.
 */
export function deriveFlowStep(ix: Interaction): FlowStep {
  const first = ix.steps !== undefined && ix.steps.length > 0 ? ix.steps[0] : undefined;
  if (first !== undefined) {
    return {
      from: ix.initiator.id,
      to: ix.target.id,
      dir: first.direction,
      verdict: first.verdict,
      checks: { identity: first.checks.identity, trust: first.checks.trust, scope: first.checks.scope },
      latency: first.latencyMs ?? ix.runtime,
      ...(first.epoch !== undefined ? { epoch: first.epoch } : {}),
      interactionId: ix.id,
      spanId: spanIdFor(ix.id),
    };
  }
  return {
    from: ix.initiator.id,
    to: ix.target.id,
    dir: "request",
    verdict: ix.threat ? "blocked" : "allowed",
    checks: { identity: true, trust: !ix.threat, scope: !ix.threat },
    latency: ix.runtime,
    interactionId: ix.id,
    spanId: spanIdFor(ix.id),
  };
}

/**
 * Compute the seal set unconditionally (Requirement 4.3): one `SealEdge` from the last hop's target
 * to the synthetic provenance node when the intent has a non-empty `provenanceRecordID` AND there is
 * at least one hop; otherwise an empty set. A seal inherits the threat flag of the last hop so it
 * reads consistently with the rest of the graph.
 */
export function buildSealEdges(
  provenanceRecordID: string,
  interactions: readonly Interaction[],
): readonly SealEdge[] {
  const last = interactions[interactions.length - 1];
  if (provenanceRecordID === "" || last === undefined) return [];
  return [{ from: last.target.id, to: provenanceNodeId(provenanceRecordID), threat: last.threat }];
}

/**
 * Build the full flow from an intent's owner + interactions. Initiators sit in the left column,
 * agent targets in the middle, tool/app targets on the right. Edges are the interactions (threat
 * exchanges highlighted); `steps` are the timed hops; `sealEdges` is the provenance seal set.
 * Deterministic — no physics — so it renders the same every time.
 */
export function buildFlowFromIntent(
  ownerId: string,
  ownerLabel: string,
  interactions: readonly Interaction[],
  provenanceRecordID = "",
): Flow {
  const nodes = new Map<string, { label: string; kind: NodeKind; col: number; threat: boolean }>();
  const upsert = (id: string, label: string, kind: NodeKind, col: number, threat: boolean): void => {
    const prev = nodes.get(id);
    if (prev === undefined) nodes.set(id, { label, kind, col, threat });
    else if (threat) nodes.set(id, { ...prev, threat: true });
  };

  // The intent owner is the human/root initiator in the left column.
  upsert(ownerId, ownerLabel, "human", 0, false);

  const edges: FlowEdge[] = [];
  const steps: FlowStep[] = [];
  for (const ix of interactions) {
    const initCol = ix.initiator.id === ownerId ? 0 : 1;
    const initKind: NodeKind = ix.initiator.id === ownerId ? "human" : "agent";
    upsert(ix.initiator.id, ix.initiator.name, initKind, initCol, ix.threat);
    const targetKind: NodeKind = ix.targetType === "tool" ? "tool" : "agent";
    const targetCol = ix.targetType === "tool" ? 2 : 1;
    upsert(ix.target.id, ix.target.name, targetKind, targetCol, ix.threat);
    edges.push({ id: ix.id, from: ix.initiator.id, to: ix.target.id, threat: ix.threat });
    steps.push(deriveFlowStep(ix));
  }

  // Place nodes column by column, stacking vertically within each column.
  const perCol = new Map<number, number>();
  const placed: FlowNode[] = [];
  for (const [id, n] of nodes) {
    const slot = perCol.get(n.col) ?? 0;
    perCol.set(n.col, slot + 1);
    placed.push({
      id,
      label: n.label,
      kind: n.kind,
      col: n.col,
      threat: n.threat,
      x: COL_X[n.col] ?? COL_X[2],
      y: TOP + slot * ROW_GAP,
    });
  }

  const tallest = Math.max(1, ...[...perCol.values()]);
  const sealEdges = buildSealEdges(provenanceRecordID, interactions);
  // The seal/provenance node is drawn one ROW_GAP BELOW its source hop (IntentFlowPage places it at
  // `source.y + ROW_GAP`). Reserve that extra row in the intrinsic height when a seal exists, so the
  // viewBox contains it and `preserveAspectRatio="xMidYMid meet"` never clips the Provenance Layer
  // node. Without a seal the height is unchanged.
  const hasSeal = sealEdges.length > 0;
  // The seal node now sits in its OWN lane to the right (PROV_X) on the source's row, so it no longer
  // needs an extra stacked row below — but the viewBox must be wide enough to contain that lane.
  const height = TOP + tallest * ROW_GAP + 24;
  const width = (hasSeal ? PROV_X : COL_X[2]) + NODE_W + 48;
  return { nodes: placed, edges, steps, sealEdges, width, height };
}

/**
 * Group timed hops into playback rounds (Requirements 5.1, 5.2). Returns an array of rounds, each a
 * list of STEP INDICES into `steps`, preserving input order of both steps and rounds.
 *
 * - 5.1 — Two steps share a round iff they carry the SAME defined `epoch`, fan out from the SAME
 *   initiator (`from`), and are BOTH `request` hops. A step grows the current round only when it
 *   matches the round's open key on all three axes; anything else opens a fresh round. Responses and
 *   hops with a different epoch (or different initiator) therefore always land in a separate round.
 * - 5.2 — A step with no `epoch` is always its own round (its key never matches another step's), so
 *   when NO step carries an epoch the result is one round per hop in input order. Only steps that
 *   actually share a defined epoch + initiator + request direction ever group together.
 */
export function groupParallelRounds(steps: readonly FlowStep[]): number[][] {
  const rounds: number[][] = [];
  // The "open" round's grouping key, or null when the previous step cannot be joined.
  let openKey: string | null = null;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (s === undefined) continue;
    const groupable = s.epoch !== undefined && s.dir === "request";
    const key = groupable ? `${s.epoch}\u0000${s.from}` : null;
    const open = rounds[rounds.length - 1];
    if (key !== null && key === openKey && open !== undefined) {
      open.push(i);
    } else {
      rounds.push([i]);
      openKey = key;
    }
  }
  return rounds;
}
